<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1100px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Leave Entitlement Setting"
      text="Edit Leave Entitlement Setting"
      class="mb-0"
    > 
      <form v-if="dialog">
        <v-container>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="localLeaveEntitlementSetting.name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Name"
              />
            </v-col>

            <v-col cols="6">
              <v-autocomplete 
                v-model="localLeaveEntitlementSetting.leave_type_id"
                v-validate=" 'required'"
                :items="leaveTypes"
                :error-messages="errors.collect('leave_type_id')"
                data-vv-name="leave_type_id"      
                data-vv-as="leave type"
                label="Leave Type"
                item-text="name"      
                item-value="id"      
              />
            </v-col>
          </v-row>
      
          <v-row>
            <v-col>
              <span class="font-weight-bold pr-2">Leave Entitlement Setting Details:</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon
                    color="primary"
                    v-on="{ ...dialog, ...tooltip }"
                    @click="addRow"
                  >
                    mdi-plus
                  </v-icon>
                </template>
                <span>Add new row</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row
            v-for="(detail,counter) in localLeaveEntitlementSetting.leave_entitlement_setting_details"
            :key="counter"
          >
            <v-col class="py-0">
              <v-text-field
                v-model="detail.start_day"
                v-validate="'required|negativeValue|startDay'"
                :name="'start_day' + counter"
                :error-messages="errors.collect('start_day' + counter)"
                data-vv-delay="500"
                data-vv-as="start day"
                type="number"
                label="Start Day"
                :min="0"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="detail.end_day"
                v-validate="'required|negativeValue|endDay'"
                :name="'end_day' + counter"
                :error-messages="errors.collect('end_day' + counter)"
                data-vv-delay="500"
                data-vv-as="end day"
                type="number"
                label="End Day"
                :min="0"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
            <v-col class="py-0">
              <v-text-field
                v-model="detail.days"
                v-validate="'required|negativeValue|dayValidator'"
                :name="'days' + counter"
                :error-messages="errors.collect('days' + counter)"
                data-vv-delay="500"
                data-vv-as="days"
                type="number"
                label="Days"
                :min="0"
                @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>
            <v-col 
              v-if="localLeaveEntitlementSetting.leave_type_id === 4 || localLeaveEntitlementSetting.leave_type_id === 5"
              class="py-0"
            >
              <v-text-field
                v-model="detail.official_days"
                v-validate="'required|negativeValue|dayValidator'"
                :name="'days' + counter"
                :error-messages="errors.collect('days' + counter)"
                data-vv-delay="500"
                data-vv-as="days"
                type="number"
                label="Official Days in Labour Act"
                :min="0"
                @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col class="py-0">
              <v-text-field
                v-model="detail.maximum_carry_forward_days"
                v-validate="'required|negativeValue'"
                :name="'maximum_carry_forward_days' + counter"
                :error-messages="errors.collect('maximum_carry_forward_days' + counter)"
                data-vv-delay="500"
                data-vv-as="maximum carry forward days"
                type="number"
                label="Maximum Carry Forward Days"
                :min="0"
                @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col class="text-center">
              <v-icon
                color="error"
                @click="deleteRow(counter)"
              >
                mdi-window-close
              </v-icon>
            </v-col> 
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      setting: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localLeaveEntitlementSetting: {},
        leaveTypes: [],
      }
    },

    watch: {
      dialog: function (value) {
        if (!value) return       
        this.loadLeaveTypes()
        this.localLeaveEntitlementSetting = { ...this.setting }
        let _this = this

        this.$validator.extend('startDay', {
          getMessage (field, args) {
            return 'Invalid start day'
          },
          validate (value, args) {
            let valid = true
           
            for (let i = 0; i < _this.localLeaveEntitlementSetting.leave_entitlement_setting_details.length; i++) {
              if (i > 0) {
                if (
                  ((_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day !== null) && (parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day) <= parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].start_day))) ||
                  ((_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].end_day !== null) && (parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day) <= parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].end_day)))
                ) {
                  valid = false
                  break
                }
              } 
            }
          
            return valid
          },
        })

        this.$validator.extend('endDay', {
          getMessage (field, args) {
            return 'Invalid end day'
          },
          validate (value, args) {
            let valid = true

            for (let i = 0; i < _this.localLeaveEntitlementSetting.leave_entitlement_setting_details.length; i++) {
              if (i > 0) {
                if (
                  (_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].end_day !== null && (parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].end_day) < parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day))) ||
                  (_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day !== null && (parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].end_day) <= parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].start_day)))
                ) {
                  valid = false
                  break
                }
              }
            }

            return valid
          },
        })

        this.$validator.extend('dayValidator', {
          getMessage (field, args) {
            return 'Invalid days'
          },
          validate (value, args) {
            let valid = true

            for (let i = 0; i < _this.localLeaveEntitlementSetting.leave_entitlement_setting_details.length; i++) {
              if (i > 0) {
                if (
                  (_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].days !== null && (parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i].days) < parseInt(_this.localLeaveEntitlementSetting.leave_entitlement_setting_details[i - 1].days)))
                ) {
                  valid = false
                  break
                }
              }
            }

            return valid
          },
        })
      },
    },

    mounted () {
      let _this = this

      this.$validator.extend('negativeValue', {
        getMessage (field, args) {
          return 'Invalid Number'
        },
        validate (value, args) {
          return _this.isValidNumbers(value)
        },
      })
    },

    methods: {
      submitForm () {
        let _this = this

        this.$validator.validateAll()
          .then(result => {
            if (!result) return 
        
            this.progressing = true

            this.$api.leaveEntitlementSettings().update(this.localLeaveEntitlementSetting.id, this.prepareEntitlementSettingFormData())
              .then(res => {
                this.$api.leaveEntitlementSettingDetails().update(_this.localLeaveEntitlementSetting.id, this.prepareEntitlementSettingDetailsFormData())
                  .then(res => {
                    this.$emit('updated')
                    this.progressing = false
                    this.dialog = false
                    this.$snack({ message: 'Leave Entitlement Setting Updated Successfully' })
                  })
              })
          })
          .catch(() => {})
          .then(() => {})
      },

      prepareEntitlementSettingFormData () {
        return {
          name: this.localLeaveEntitlementSetting.name,
          leave_type_id: this.localLeaveEntitlementSetting.leave_type_id,
        }
      },

      prepareEntitlementSettingDetailsFormData () {
        return {
          details: this.localLeaveEntitlementSetting.leave_entitlement_setting_details,
        }
      },

      addRow () {
        this.localLeaveEntitlementSetting.leave_entitlement_setting_details.push({ start_day: null, end_day: null, days: null, maximum_carry_forward_days: 0 })
      },

      deleteRow (counter) {
        this.localLeaveEntitlementSetting.leave_entitlement_setting_details.splice(counter, 1)
      },

      isValidNumbers (value) {
        return value >= 0
      },
  
      loadLeaveTypes () {
        this.$api.hrisLeaveTypes().list()
          .then(res => {
            this.leaveTypes = res.data.filter(leaveType => {
              return leaveType.is_optional_leave_entitlement === 0
            }) 
          })
      },
    },
  }
</script>
